import React, { useState, useEffect } from 'react'

import Sidebar from 'components/Sidebar';
import Avatar from 'components/Avatar';
import { useUser } from 'utils/userContext';

import { supabase } from 'utils/supabaseClient';

function Admin() {

    const { user, loading } = useUser();
    const [users, setUsers] = useState([]);
    const [lessonData, setLessonData] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [editingUser, setEditingUser] = useState(null);
    const [editForm, setEditForm] = useState({
        currentUnit: 1,
        currentConcept: 1,
        currentConceptProgress: 1,
        maxUnit: null,
        maxConcept: null,
    });

    // Fetch lesson data structure
    useEffect(() => {
        const fetchLessonData = async () => {
            try {
                const response = await fetch('/lessons.json');
                const lessons = await response.json();
                setLessonData(lessons);
            } catch (error) {
                console.error("Error fetching lesson data:", error);
            }
        };
        fetchLessonData();
    }, []);

    // Fetch all users
    useEffect(() => {
        const fetchUsers = async () => {
            if (!user?.is_admin) return;

            setLoadingUsers(true);
            try {
                const { data, error } = await supabase
                    .from('users')
                    .select('id, username, email, currentUnit, currentConcept, currentConceptProgress, lessonGoal, timeGoal, is_admin, created_at, maxUnit, maxConcept')
                    .order('created_at', { ascending: false });

                if (error) throw error;
                setUsers(data || []);
            } catch (error) {
                console.error("Error fetching users:", error);
                // If error is due to missing columns, try without them
                if (error.message?.includes('maxUnit') || error.message?.includes('maxConcept')) {
                    console.warn("maxUnit/maxConcept columns not found - please run the migration");
                    try {
                        const { data, error: fallbackError } = await supabase
                            .from('users')
                            .select('id, username, email, currentUnit, currentConcept, currentConceptProgress, lessonGoal, timeGoal, is_admin, created_at')
                            .order('created_at', { ascending: false });

                        if (!fallbackError) setUsers(data || []);
                    } catch (fallbackError) {
                        console.error("Fallback fetch also failed:", fallbackError);
                    }
                }
            } finally {
                setLoadingUsers(false);
            }
        };

        if (!loading && user?.is_admin) {
            fetchUsers();
        }
    }, [loading, user?.is_admin]);

    const handleEditUser = (userData) => {
        setEditingUser(userData);
        setEditForm({
            currentUnit: userData.currentUnit || 1,
            currentConcept: userData.currentConcept === 999 ? 'test' : userData.currentConcept || 1,
            currentConceptProgress: userData.currentConceptProgress || 1,
            maxUnit: userData.maxUnit || null,
            maxConcept: userData.maxConcept || null,
        });
    };

    const handleSaveUser = async () => {
        if (!editingUser) return;

        try {
            const updates = {
                currentUnit: parseInt(editForm.currentUnit),
                currentConcept: editForm.currentConcept === 'test' ? 999 : parseInt(editForm.currentConcept),
                currentConceptProgress: parseInt(editForm.currentConceptProgress),
                maxUnit: editForm.maxUnit ? parseInt(editForm.maxUnit) : null,
                maxConcept: editForm.maxConcept ? parseInt(editForm.maxConcept) : null,
            };

            const { error } = await supabase
                .from('users')
                .update(updates)
                .eq('id', editingUser.id);

            if (error) throw error;

            // Update local state
            setUsers(users.map(u =>
                u.id === editingUser.id
                    ? { ...u, ...updates }
                    : u
            ));

            setEditingUser(null);
        } catch (error) {
            console.error("Error updating user:", error);
            alert("Failed to update user: " + error.message);
        }
    };

    const getConceptName = (unitNum, conceptNum) => {
        if (conceptNum === 999) return "Test";
        const unit = lessonData[unitNum - 1];
        if (!unit) return `Concept ${conceptNum}`;
        const concept = unit.concepts?.[conceptNum - 1];
        return concept?.concept || `Concept ${conceptNum}`;
    };

    const getUnitName = (unitNum) => {
        const unit = lessonData[unitNum - 1];
        return unit?.unit || `Unit ${unitNum}`;
    };

    const filteredUsers = users.filter(u =>
        u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading || !user?.is_admin) {
        return (
            <div className="flex flex-row items-center h-screen gap-0 bg-background p-4 lg:p-8 lg:pr-0 pr-0">
                <Sidebar />
                <div className="flex-grow h-full bg-background text-text flex items-center justify-center">
                    <h1 className="text-2xl">Access Denied</h1>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-row items-center h-screen gap-0 bg-background p-4 lg:p-8 lg:pr-0 pr-0" >
            <Sidebar></Sidebar>
            <div className='flex-grow h-full bg-background text-text overflow-hidden flex flex-col' >
                {/* Header */}
                <div className="flex flex-col px-6 py-4 border-b-2 border-border">
                    <div className="flex items-center justify-between mb-4">
                        <h1 className='font-bold text-[1.8rem] lg:text-[2rem]'>User Management</h1>
                        <Avatar size={"xs"} showRings={false} dropdown={true} />
                    </div>

                    {/* Search Bar */}
                    <div className="flex items-center gap-4">
                        <input
                            type="text"
                            placeholder="Search by username or email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="flex-grow bg-background-light border-2 border-border focus:border-border-light rounded-xl px-4 py-2 outline-none text-text"
                        />
                        <span className="text-text-light font-medium">
                            {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                </div>

                {/* User List */}
                <div className="flex-grow overflow-y-auto px-6 py-4">
                    {loadingUsers ? (
                        <div className="flex items-center justify-center h-full">
                            <span className="text-text-light text-lg">Loading users...</span>
                        </div>
                    ) : filteredUsers.length === 0 ? (
                        <div className="flex items-center justify-center h-full">
                            <span className="text-text-light text-lg">No users found</span>
                        </div>
                    ) : (
                        <div className="grid gap-3">
                            {filteredUsers.map((userData) => (
                                <div
                                    key={userData.id}
                                    className="bg-background-light border-2 border-border hover:border-border-light rounded-xl p-4 transition-colors"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex-grow grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                            {/* User Info */}
                                            <div className="flex flex-col">
                                                <span className="text-xs text-text-light font-medium">USER</span>
                                                <span className="font-bold text-text">{userData.username}</span>
                                                <span className="text-sm text-text-light">{userData.email}</span>
                                                {userData.is_admin && (
                                                    <span className="text-xs text-accent font-bold mt-1">ADMIN</span>
                                                )}
                                            </div>

                                            {/* Current Progress */}
                                            <div className="flex flex-col">
                                                <span className="text-xs text-text-light font-medium">CURRENT PROGRESS</span>
                                                <span className="text-sm font-bold text-primary">
                                                    Unit {userData.currentUnit}: {getUnitName(userData.currentUnit)}
                                                </span>
                                                <span className="text-sm font-medium text-text">
                                                    {getConceptName(userData.currentUnit, userData.currentConcept)}
                                                </span>
                                                <span className="text-xs text-text-light">
                                                    Progress: {userData.currentConceptProgress || 1}
                                                </span>
                                            </div>

                                            {/* Access Limits */}
                                            <div className="flex flex-col">
                                                <span className="text-xs text-text-light font-medium">ACCESS LIMITS</span>
                                                {userData.maxUnit || userData.maxConcept ? (
                                                    <>
                                                        <span className="text-sm font-medium text-warning">
                                                            Max Unit: {userData.maxUnit || 'Unlimited'}
                                                        </span>
                                                        <span className="text-sm font-medium text-warning">
                                                            Max Concept: {userData.maxConcept || 'Unlimited'}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <span className="text-sm text-text-light">No restrictions</span>
                                                )}
                                            </div>

                                            {/* Goals */}
                                            <div className="flex flex-col">
                                                <span className="text-xs text-text-light font-medium">GOALS</span>
                                                <span className="text-sm font-medium text-text">
                                                    Time: {userData.timeGoal || 30} min/day
                                                </span>
                                                <span className="text-sm font-medium text-text">
                                                    Lessons: {userData.lessonGoal || 7}/day
                                                </span>
                                            </div>
                                        </div>

                                        {/* Edit Button */}
                                        <button
                                            onClick={() => handleEditUser(userData)}
                                            className="ml-4 px-4 py-2 bg-primary hover:bg-opacity-80 text-white font-bold rounded-lg transition-all"
                                        >
                                            Edit
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Edit Modal */}
            {editingUser && (
                <div
                    className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'
                    onMouseDown={(e) => {
                        if (e.target === e.currentTarget) {
                            e.target.dataset.clickStart = 'true';
                        }
                    }}
                    onMouseUp={(e) => {
                        if (e.target === e.currentTarget && e.target.dataset.clickStart === 'true') {
                            setEditingUser(null);
                        }
                        delete e.target.dataset.clickStart;
                    }}
                >
                    <div className='bg-background-pop rounded-2xl p-6 max-w-2xl w-full mx-4 border-2 border-border'>
                        <h2 className='text-2xl font-bold mb-6 text-text'>Edit User: {editingUser.username}</h2>

                        <div className='flex flex-col gap-6 mb-6'>
                            {/* Current Progress Section */}
                            <div className='flex flex-col gap-4'>
                                <h3 className='text-sm font-bold text-primary uppercase tracking-wide'>Current Progress</h3>

                                {/* Unit, Concept, and Progress in one row */}
                                <div className='grid grid-cols-3 gap-4'>
                                    <div className='flex flex-col'>
                                        <label className='text-sm font-bold text-text-light mb-2'>Unit</label>
                                        <input
                                            type="number"
                                            min="1"
                                            max={lessonData.length}
                                            value={editForm.currentUnit}
                                            onChange={(e) => setEditForm({ ...editForm, currentUnit: e.target.value })}
                                            className='bg-background border-2 border-border focus:border-primary rounded-lg px-4 py-2 outline-none text-text font-bold'
                                        />
                                        <span className='text-xs text-text-light mt-1'>
                                            {getUnitName(parseInt(editForm.currentUnit))}
                                        </span>
                                    </div>

                                    <div className='flex flex-col'>
                                        <label className='text-sm font-bold text-text-light mb-2'>Concept</label>
                                        <input
                                            type="number"
                                            min="1"
                                            max={lessonData[parseInt(editForm.currentUnit) - 1]?.concepts?.length || 999}
                                            value={editForm.currentConcept === 'test' ? '' : editForm.currentConcept}
                                            onChange={(e) => {
                                                setEditForm({ ...editForm, currentConcept: e.target.value || 1 });
                                            }}
                                            className='bg-background border-2 border-border focus:border-primary rounded-lg px-4 py-2 outline-none text-text font-bold'
                                            placeholder="1, 2, 3..."
                                        />
                                        {(() => {
                                            const maxConcepts = lessonData[parseInt(editForm.currentUnit) - 1]?.concepts?.length || 999;
                                            const currentConcept = editForm.currentConcept === 'test' ? 999 : parseInt(editForm.currentConcept);

                                            if (currentConcept > maxConcepts) {
                                                return (
                                                    <span className='text-xs text-warning mt-1 font-bold'>
                                                        ⚠ Unit {editForm.currentUnit} only has {maxConcepts} concept{maxConcepts !== 1 ? 's' : ''}
                                                    </span>
                                                );
                                            }

                                            return (
                                                <span className='text-xs text-text-light mt-1'>
                                                    {getConceptName(parseInt(editForm.currentUnit), currentConcept)}
                                                </span>
                                            );
                                        })()}
                                    </div>

                                    {/* Concept Progress with roadmap-style dots */}
                                    <div className='flex flex-col'>
                                        <label className='text-sm font-bold text-text-light mb-2'>Progress</label>
                                        <div className='flex items-center gap-2 h-[42px]'>
                                            {[1, 2, 3].map((num) => (
                                                <button
                                                    key={num}
                                                    onClick={() => setEditForm({ ...editForm, currentConceptProgress: num })}
                                                    className='relative group outline-none focus:outline-none'
                                                    title={`${num} time${num > 1 ? 's' : ''} completed`}
                                                >
                                                    <div
                                                        className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ease-in-out
                                                            ${parseInt(editForm.currentConceptProgress) >= num
                                                                ? 'scale-100 shadow-lg'
                                                                : 'scale-90 bg-background-light border-2 border-border group-hover:border-accent/50 group-hover:scale-95'
                                                            }`}
                                                        style={{
                                                            background: parseInt(editForm.currentConceptProgress) >= num
                                                                ? 'linear-gradient(135deg, var(--accent) 0%, #10b981 100%)'
                                                                : undefined,
                                                            boxShadow: parseInt(editForm.currentConceptProgress) >= num
                                                                ? '0 4px 12px rgba(16, 185, 129, 0.3)'
                                                                : undefined,
                                                        }}
                                                    >
                                                        <span className={`transition-colors duration-300 ${parseInt(editForm.currentConceptProgress) >= num ? 'text-white' : 'text-text-light'}`}>
                                                            {num}
                                                        </span>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                        <span className='text-xs text-text-light mt-1'>
                                            Times completed
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Divider */}
                            <div className='border-t border-border'></div>

                            {/* Access Limits Section */}
                            <div className='flex flex-col gap-4'>
                                <h3 className='text-sm font-bold text-warning uppercase tracking-wide'>Access Limits</h3>

                                <div className='grid grid-cols-2 gap-4'>
                                    {/* Max Unit */}
                                    <div className='flex flex-col'>
                                        <label className='text-sm font-bold text-text-light mb-2'>Max Unit</label>
                                        <input
                                            type="number"
                                            min="1"
                                            max={lessonData.length}
                                            value={editForm.maxUnit || ''}
                                            onChange={(e) => {
                                                const newMaxUnit = e.target.value || null;
                                                setEditForm({
                                                    ...editForm,
                                                    maxUnit: newMaxUnit,
                                                    maxConcept: newMaxUnit ? editForm.maxConcept : null
                                                });
                                            }}
                                            placeholder="Unlimited"
                                            className='bg-background border-2 border-border focus:border-warning rounded-lg px-4 py-2 outline-none text-text font-bold'
                                        />
                                        <span className='text-xs text-text-light mt-1'>
                                            {editForm.maxUnit ? getUnitName(parseInt(editForm.maxUnit)) : 'Leave empty for no limit'}
                                        </span>
                                    </div>

                                    {/* Max Concept */}
                                    <div className='flex flex-col'>
                                        <label className='text-sm font-bold text-text-light mb-2'>Max Concept</label>
                                        <input
                                            type="number"
                                            min="1"
                                            max={editForm.maxUnit ? (lessonData[parseInt(editForm.maxUnit) - 1]?.concepts?.length || 999) : 999}
                                            value={editForm.maxConcept || ''}
                                            onChange={(e) => {
                                                setEditForm({ ...editForm, maxConcept: e.target.value || null });
                                            }}
                                            placeholder={editForm.maxUnit ? "Unlimited" : "Set Max Unit first"}
                                            disabled={!editForm.maxUnit}
                                            className='bg-background border-2 border-border focus:border-warning rounded-lg px-4 py-2 outline-none text-text font-bold disabled:opacity-40 disabled:cursor-not-allowed'
                                        />
                                        {(() => {
                                            if (!editForm.maxUnit) {
                                                return (
                                                    <span className='text-xs text-text-light mt-1'>
                                                        Set Max Unit first
                                                    </span>
                                                );
                                            }

                                            const maxConcepts = lessonData[parseInt(editForm.maxUnit) - 1]?.concepts?.length || 999;
                                            const currentMaxConcept = parseInt(editForm.maxConcept);

                                            if (editForm.maxConcept && currentMaxConcept > maxConcepts) {
                                                return (
                                                    <span className='text-xs text-warning mt-1 font-bold'>
                                                        ⚠ Unit {editForm.maxUnit} only has {maxConcepts} concept{maxConcepts !== 1 ? 's' : ''}
                                                    </span>
                                                );
                                            }

                                            return (
                                                <span className='text-xs text-text-light mt-1'>
                                                    {editForm.maxConcept
                                                        ? getConceptName(parseInt(editForm.maxUnit), currentMaxConcept)
                                                        : 'Leave empty for no limit'
                                                    }
                                                </span>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Buttons */}
                        <div className='flex justify-end gap-3 pt-2 border-t border-border'>
                            <button
                                className='px-6 py-2 border-2 border-border text-text font-bold rounded-lg hover:bg-background transition-all'
                                onClick={() => setEditingUser(null)}
                            >
                                Cancel
                            </button>
                            <button
                                className='px-6 py-2 bg-primary hover:bg-opacity-80 text-white font-bold rounded-lg transition-all'
                                onClick={handleSaveUser}
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div >
    )
}


export default Admin